// PlaylistsPage.tsx
import React, { useState, useEffect } from 'react';
import { Plus, List, Clock, Calendar, AlertTriangle, Trash2, MoreVertical } from 'lucide-react';
import { playlistsApi } from '../services/playlistsApi';
import { Playlist } from '../types/playlists';
import PlaylistManager from '../components/PlaylistManager';

const PlaylistsPage = () => {
  const [playlists, setPlaylists] = useState<Playlist[]>([]);
  const [selectedPlaylist, setSelectedPlaylist] = useState<Playlist | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [newPlaylistName, setNewPlaylistName] = useState('');

  useEffect(() => {
    fetchPlaylists();
  }, []);

  const fetchPlaylists = async () => {
    try {
      setLoading(true);
      const data = await playlistsApi.getAllPlaylists();
      setPlaylists(data);
      setError(null);
    } catch (err) {
      setError('Failed to fetch playlists');
      console.error('Error fetching playlists:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (playlistId: number) => {
    try {
      setIsDeleting(true);
      await playlistsApi.deletePlaylist(playlistId);
      await fetchPlaylists();
      setShowDeleteConfirm(null);
    } catch (err) {
      setError('Failed to delete playlist');
      console.error('Error deleting playlist:', err);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCreatePlaylist = () => {
    if (!newPlaylistName.trim()) {
      setError('Please enter a playlist name');
      return;
    }
    setSelectedPlaylist({
      playlistId: 0,
      playlistName: newPlaylistName,
      createdBy: 1,
      createdDateTime: new Date(),
      active: true,
      playlistItems: []
    });
    setShowCreateModal(false);
    setNewPlaylistName('');
  };

  // Create Playlist Modal
  const CreatePlaylistModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <h2 className="text-xl font-semibold mb-4">Create New Playlist</h2>
        <input
          type="text"
          value={newPlaylistName}
          onChange={(e) => setNewPlaylistName(e.target.value)}
          placeholder="Enter playlist name"
          className="w-full px-3 py-2 border rounded-lg mb-4"
          autoFocus
        />
        <div className="flex justify-end space-x-3">
          <button
            onClick={() => {
              setShowCreateModal(false);
              setNewPlaylistName('');
            }}
            className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded"
          >
            Cancel
          </button>
          <button
            onClick={handleCreatePlaylist}
            disabled={!newPlaylistName.trim()}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
          >
            Create
          </button>
        </div>
      </div>
    </div>
  );

  // Delete Confirmation Modal
  const DeleteConfirmModal = ({ playlistId }: { playlistId: number }) => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <div className="flex items-center mb-4">
          <AlertTriangle className="w-6 h-6 text-red-600 mr-2" />
          <h3 className="text-lg font-medium">Delete Playlist</h3>
        </div>
        <p className="text-gray-600 mb-4">
          Are you sure you want to delete this playlist? This action cannot be undone.
        </p>
        <div className="flex justify-end space-x-3">
          <button
            onClick={() => setShowDeleteConfirm(null)}
            className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded"
            disabled={isDeleting}
          >
            Cancel
          </button>
          <button
            onClick={() => handleDelete(playlistId)}
            disabled={isDeleting}
            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );

  if (selectedPlaylist) {
    return (
      <PlaylistManager
        playlist={selectedPlaylist}
        onSave={async (updatedPlaylist) => {
          await fetchPlaylists();
          setSelectedPlaylist(null);
        }}
        onCancel={() => setSelectedPlaylist(null)}
      />
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-800">Playlists</h1>
          <p className="text-gray-600">Manage your content playlists</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Playlist
        </button>
      </div>

      {error && (
        <div className="p-4 mb-6 text-red-700 bg-red-100 rounded-lg">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {loading ? (
          <div className="col-span-full text-center py-8 text-gray-500">
            Loading playlists...
          </div>
        ) : playlists.length === 0 ? (
          <div className="col-span-full text-center py-8 text-gray-500">
            No playlists found
          </div>
        ) : (
          playlists.map((playlist) => (
            <div
              key={playlist.playlistId}
              className="bg-white rounded-lg shadow hover:shadow-md transition-shadow"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">{playlist.playlistName}</h3>
                  <div className="flex items-center space-x-2">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      playlist.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {playlist.active ? 'Active' : 'Inactive'}
                    </span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowDeleteConfirm(playlist.playlistId);
                      }}
                      className="p-1 text-gray-400 hover:text-red-600 rounded"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center text-sm text-gray-500">
                    <List className="w-4 h-4 mr-2" />
                    <span>{playlist.playlistItems?.length || 0} items</span>
                  </div>

                  <div className="flex items-center text-sm text-gray-500">
                    <Clock className="w-4 h-4 mr-2" />
                    <span>
                      Created {new Date(playlist.createdDateTime).toLocaleDateString()}
                    </span>
                  </div>
                </div>

                <button
                  onClick={() => setSelectedPlaylist(playlist)}
                  className="mt-4 w-full px-4 py-2 text-blue-600 hover:bg-blue-50 rounded"
                >
                  Edit Playlist
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {showCreateModal && <CreatePlaylistModal />}
      {showDeleteConfirm !== null && <DeleteConfirmModal playlistId={showDeleteConfirm} />}
    </div>
  );
};

export default PlaylistsPage;